import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Route guard — redirects unauthenticated users to /auth?next=<path>.
 * Used to wrap deep pages that require login (match details, weekend
 * slate, accuracy, history, etc.) under the Iter 82 tease-mode launch.
 */
export const RequireAuth = ({ children }) => {
  const { token, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !token) {
      toast.info("Sign up to unlock this page", { duration: 2500 });
    }
  }, [loading, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground" />
      </div>
    );
  }
  // Iter 89 — Trust the token alone.  When `/auth/me` fails on a
  // transient 5xx the user object is null but the JWT is still valid;
  // forcing a redirect would log the user out on every prod hiccup.
  // The heartbeat in AuthContext re-fetches /auth/me every 5 min and
  // on focus/online events, so `user` populates as soon as prod
  // recovers — no UX cost.
  if (!token) {
    const next = encodeURIComponent(location.pathname + (location.search || ""));
    return <Navigate to={`/auth?next=${next}&mode=register`} replace />;
  }
  return children;
};

export default RequireAuth;
